import type { ContractRecordMap } from '@libs/data-access/entities'
import {
  isContractInProgressBetween,
  membershipService
} from '@libs/data-access/queries'
import { Button, Hide, Modal } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { CUSTOM } from '@spa/constants/RestTypes'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import { LockedAlert } from '@spa/scenes/Plannings/Forms/components'
import { Step } from '@spa/scenes/Plannings/Modals/RestModal/redux/modules/main'
import { getParams } from '@spa/scenes/Plannings/redux/selectors'
import { getEndDateISO, getStartDateISO } from '@spa/services/rests'
import { isEqual } from 'lodash-es'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { findAndMergeAmendmentsRelativeToDate } from 'snap-redux/modules/contracts'
import { fetchCountersByLocationId } from 'snap-redux/modules/counters'
import { getCountryConfiguration } from 'snap-redux/modules/countryConfigurations/selectors'
import { getLocationById } from 'snap-redux/modules/locations/selectors'

import { RevaluationTableForm } from '../../RevaluationTableForm/RevaluationTableForm'

import {
  baseStructuredSelector,
  currentUserSelector
} from './RestFormSection.selectors'
import { AccidentDate } from './components/AccidentDate/AccidentDate'
import { CounterWithdrawalForm } from './components/CounterWithdrawalForm/CounterWithdrawalForm'
import { DefaultRestDurationOrRevaluation } from './components/DefaultRestDurationOrRevaluation/DefaultRestDurationOrRevaluation'
import { DurationFields } from './components/DurationFields/DurationFields'
import { EditForm } from './components/FormSubmitSection/EditForm/EditForm'
import { NoteField } from './components/NoteField/NoteField'
import RestTypeAndContract from './components/RestTypeAndContract/RestTypeAndContract'
import { useRevalo } from './hooks'
import type { OwnProps } from './interface'
import { getSelectableRestTypes } from './utils'

export const RestFormSection: FC<OwnProps> = ({
  onFormSuccess,
  plannableOverlapExtended,
  rest,
  formData,
  selectedRest,
  selectedRestType,
  finalContractsIds,
  restTypeFormValue,
  currentStep,
  revaluatedData,
  revaluatedInputData,
  setRevaluatedInputData,
  setRestCounterOperationDuration,
  userContractIdFormValue,
  initialContract,
  userContractsSize,
  restDurationOption,
  handleRestOptionChange,
  selectedRestUsesCounter,
  contractsData,
  hasAccidentDate
}) => {
  const { tCommon } = usePlanningFormTranslation()

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext()

  const [formLoading, setFormLoading] = useState(false)
  const [formDisabled, setFormDisabled] = useState(false)
  const [, setFormError] = useState(null)

  const isLoading = useSelector(
    (state: ReduxState) =>
      state.counters.getIn(['meta', 'loading']) ||
      state.shifts.getIn(['meta', 'loading']) ||
      state.rests.getIn(['meta', 'loading']),
    isEqual
  )

  /**
   * Get base data
   */
  const { account } = useSelector(
    (state: ReduxState) => baseStructuredSelector(state),
    isEqual
  )

  const { locationId } = useMemoSelector(getParams)

  const { currentMembership } = useSelector(
    (state: ReduxState) => currentUserSelector(state),
    isEqual
  )

  const planningLocation = useSelector((state: ReduxState) =>
    getLocationById(state, Number(locationId))
  )

  const canSetCustomRestValue = currentMembership.can_set_custom_rest_value
  const restId = rest.id || null

  /**
   * Filter contracts
   */
  const contracts = useMemo(
    () =>
      contractsData.filter(contract =>
        isContractInProgressBetween(
          contract,
          getStartDateISO(rest),
          getEndDateISO(rest)
        )
      ),
    [contractsData, rest]
  )

  const dispatch = useDispatch()

  useEffect(() => {
    if (locationId) {
      // @ts-ignore migration from js(x) to ts(x)
      dispatch(fetchCountersByLocationId(locationId))
    }
  }, [dispatch, locationId])

  /**
   * Apply the eventual amendments to the contract.
   */
  const mergeAmendments = useSelector((state: ReduxState) =>
    findAndMergeAmendmentsRelativeToDate(state, account, getStartDateISO(rest))
  )
  const selectedContract = mergeAmendments(initialContract || {})

  /*
   * Apply amendments
   */
  const selectedRestTypeCanBeBulked = selectedRest?.can_be_bulked

  const onSubmitError = useCallback((err: object) => {
    setFormLoading(false)
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    setFormError(err)
  }, [])

  const shouldShowRCRForPartTime = Boolean(
    planningLocation?.enable_compensatory_for_part_time
  )

  const countryConfiguration = useMemoSelector(getCountryConfiguration)

  const today = moment().format('YYYY-MM-DD')
  const getRestDate = watch('dateRange.start.date')
  const timeoffEligibilityQuery = membershipService.timeoff.useFetchEligibility(
    {
      pathParams: { membership_id: selectedContract.membership_id },
      queryParams: {
        effective_date: getRestDate || today
      }
    },
    {
      enabled: Boolean(selectedContract.membership_id) && Boolean(getRestDate)
    }
  )
  const isEligibleToRtt = timeoffEligibilityQuery?.data?.rtt?.eligible

  const {
    handleRevaluatedInputDataChange,
    handleResetDefaultRestDuration,
    defaultRestDuration,
    defaultRestDurationInDays,
    revaluatedRestDuration,
    restCanHaveCustomDuration
  } = useRevalo({
    onSubmitError,
    setRestOption: handleRestOptionChange,
    setRestCounterOperationDuration,
    rest,
    restDurationOption,
    restTypeFormValue,
    selectedRest,
    selectedRestType,
    setFormError,
    setFormLoading,
    userContractIdFormValue,
    formData,
    revaluatedData,
    revaluatedInputData,
    setRevaluatedInputData
  })

  const selectedRestIsLocked = Boolean(rest.locked_at)
  // Rests that use any type of counter can only be created or deleted (no update).

  const selectedRestCantBeEdited = Boolean(restId) && selectedRestUsesCounter

  const selectableRestTypes = getSelectableRestTypes({
    contract: selectedContract,
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    restId,
    restUsesCounter: selectedRestUsesCounter,
    shouldShowRCRForPartTime,
    fullTimeThreshold: countryConfiguration.full_time_threshold,
    shouldHideAccidentByHours: true,
    isEligibleToRtt
  })

  const isCustom = restDurationOption === CUSTOM
  const disableCustomRest =
    Boolean(rest.custom_value?.size) && !canSetCustomRestValue
  const displayCounterForm = selectedRestUsesCounter
  const displayRevaluationTableForm = currentStep === Step.REVALUATION_FORM

  const disableSubmitConflict =
    (plannableOverlapExtended.contracts_only_shift_overlap?.length ?? 0) > 0 &&
    selectedRestType === 'day'
      ? !formData.shift_conflict_strategy
      : false

  const disableSubmit =
    formData.hasError ||
    formLoading ||
    isLoading ||
    formDisabled ||
    selectedRestCantBeEdited ||
    !formData?.rest_type?.value ||
    !finalContractsIds.length ||
    disableSubmitConflict

  return (
    <>
      <Hide hide={displayRevaluationTableForm}>
        <>
          <Modal.Content className='overflow-visible'>
            <div className='grid gap-3'>
              {selectedRestIsLocked && <LockedAlert />}
              <RestTypeAndContract
                setValue={setValue}
                restTypeOptions={selectableRestTypes}
                restIsLocked={selectedRestIsLocked}
                isCustom={isCustom}
                selectedContracts={formData.user_contract_ids}
                plannableOverlapExtended={plannableOverlapExtended}
                restType={restTypeFormValue}
                startsAt={rest.starts_at}
                control={control}
                errors={errors}
                disabledRestTypeField={
                  Boolean(restId) && selectedRestUsesCounter
                }
                disabledContractField={selectedRestCantBeEdited}
                disableCustomRest={disableCustomRest}
                isMulti={selectedRestTypeCanBeBulked && !restId}
                contracts={contracts as ContractRecordMap}
                userContractsSize={userContractsSize}
              />
              {displayCounterForm ? (
                <CounterWithdrawalForm
                  isLoading={isLoading}
                  rest={rest}
                  restCantBeEdited={selectedRestCantBeEdited}
                  formData={formData}
                  restIsLocked={selectedRestIsLocked}
                  selectedContract={selectedContract}
                  selectedRest={selectedRest}
                  setFormDisabled={setFormDisabled}
                  plannableOverlapExtended={plannableOverlapExtended}
                  contracts={contracts as ContractRecordMap}
                  disableCustomRest={disableCustomRest}
                  defaultRestDurationInDays={defaultRestDurationInDays}
                />
              ) : (
                <DurationFields
                  contracts={contracts as ContractRecordMap}
                  plannableOverlapExtended={plannableOverlapExtended}
                  restIsLocked={selectedRestIsLocked}
                  restTypeType={selectedRestType}
                  disableCustomRest={disableCustomRest}
                />
              )}

              {restCanHaveCustomDuration && (
                <DefaultRestDurationOrRevaluation
                  rest={rest}
                  restCantBeEdited={selectedRestCantBeEdited}
                  onResetDefaultRestDuration={handleResetDefaultRestDuration}
                  defaultRestDuration={defaultRestDuration}
                  revaluatedRestDuration={revaluatedRestDuration}
                  restDurationOption={restDurationOption}
                  formLoading={formLoading}
                  canSetCustomRestValue={canSetCustomRestValue}
                />
              )}

              {hasAccidentDate && <AccidentDate />}
              <NoteField
                control={control}
                errors={errors}
                disabled={selectedRestIsLocked}
              />
            </div>
          </Modal.Content>

          <Modal.Footer className='sticky bottom-0 bg-neutral-50 pt-6'>
            {!selectedRestIsLocked && (
              <>
                {restId && (
                  <EditForm
                    rest={rest}
                    loading={formLoading || isLoading}
                    disabled={disableSubmit}
                    onSuccess={onFormSuccess}
                    onError={onSubmitError}
                  />
                )}
                {!restId && (
                  <Button
                    data-testid='create-rest'
                    fillWidth
                    loading={formLoading || isLoading}
                    type='submit'
                    disabled={disableSubmit}
                  >
                    {tCommon('action.add')}
                  </Button>
                )}
              </>
            )}
          </Modal.Footer>
        </>
      </Hide>
      <Hide hide={!displayRevaluationTableForm}>
        <RevaluationTableForm
          restIsLocked={selectedRestIsLocked}
          valuatedInputData={revaluatedInputData}
          manualValuationData={revaluatedData}
          handleRevaluatedInputDataChange={handleRevaluatedInputDataChange}
          setRestOption={handleRestOptionChange}
          restType={restTypeFormValue}
          formData={formData as any}
          isLoading={isLoading}
          isCustomized={isCustom}
        />
      </Hide>
    </>
  )
}
